/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

/**
 * Used for Suggestions tab contact option config
 */
export const CONTACT_OPTIONS = {
  /**
   * Indicates that the contact option should never be shown as a suggestion.
   */
  NEVER: 'never',
  /**
   * Indicates that the contact option should always be shown as a suggestion.
   */
  ALWAYS: 'always',
  /**
   * Indicates that the contact option should only be shown as an option if there is currently one or more strikes.
   */
  ONE_STRIKE: 'one_strike'
};

/**
 * Used for the Style tab's AI theme config. This is the value the radio buttons recognize but we're translating from
 * 'enabled' and 'disabled' to true and false since that is what the backend and web chat expect for
 * theme_config.use_ai_theme.
 */
export const AI_THEME_OPTIONS = {
  /**
   * Indicates that the AI theme should be used.
   */
  ENABLED: 'enabled',
  /**
   * Indicates that the AI theme should not be used.
   */
  DISABLED: 'disabled'
};

/**
 * Used for Style tab's carbon theme config. This is the name of the switcher tabs for carbon theme and is what we save
 * to the backend since web chat expects one of these strings.
 */
export const CARBON_THEME_OPTIONS = {
  /**
   * Indicates that the carbon g10 theme should be used.
   */
  LIGHT: 'g10',

  /**
   * Indicates that the carbon g100 theme should be used.
   */
  DARK: 'g100'
};

/**
 * Used for the HomeScreen tab's background theme config.
 */
export const HOME_SCREEN_BACKGROUND_OPTIONS = {
  /**
   * Don't add a background to the home screen.
   */
  NONE: 'none',

  /**
   * Use the accent color as a solid background on the home screen.
   */
  SOLID: 'solid',

  /**
   * Add a gradient that fades from the accent color in the top corner out to the normal chat background color.
   */
  TOP_CORNER_OUT: 'top_corner_out',

  /**
   * Add a gradient that fades from the accent color on the bottom up to the normal chat background color.
   */
  BOTTOM_UP: 'bottom_up'
};

// SMS API Version
export const API_VERSION = '2020-04-01';

// Max length constants
export const BOT_NAME_MAX_LENGTH = 40;
export const CONTACT_LABEL_MAX_LENGTH = 100;
export const GREETING_MAX_LENGTH = 90;
export const STARTERS_MAX_LENGTH = 35;
export const MAX_NUM_OF_STARTERS = 5;
export const LAUNCHER_DESKTOP_TITLE_MAX_LENGTH = 60;
export const LAUNCHER_MOBILE_TITLE_MAX_LENGTH = 45;
export const CUSTOM_EXTENSION_DESCRIPTION_MAX_LENGTH = 128;
export const CUSTOM_EXTENSION_NAME_MAX_LENGTH = 64;
export const WEB_CHAT_SIZE_MAX_LENGTH = '10000px';

// Default colors
export const WEB_CHAT_COLOR_SECONDARY = '#3D3D3D';
export const WEB_CHAT_COLOR_PRIMARY = '#FFFFFF';
export const WEB_CHAT_COLOR_ACCENT = '#0354E9';
export const TWILIO_COLOR_PRIMARY = '#F32941';
export const TWILIO_COLOR_SECONDARY = '#FFFFFF';
export const FACEBOOK_COLOR_PRIMARY = '#3f79c0';
export const FACEBOOK_MESSENGER_COLOR_PRIMARY = '#0084FF';
export const WHITE = '#FFFFFF';
export const SLACK_COLOR = '#4a154b';
export const MICROSOFT_TEAMS_COLOR = '#505AC9'; // Other options: #464EB8 OR #7B83EB

// Default web chat size values.
export const WEB_CHAT_WIDTH = '380px';
export const WEB_CHAT_HEIGHT = '640px';

// Web chat version constant
export const VERSION_TO_USE = '1';

// Used for Style tab color picker config validation
export const COLOR_REGEX = /^(#([a-f0-9]{6}|[a-f0-9]{3})\b)?$/i;

// Web chat size regex that checks if the value is a number that ranges from 1-10,000 and has the (px) unit.
export const WEB_CHAT_SIZE_REGEX = /^(10000px|[1-9][0-9]?[0-9]?[0-9]?px)?$/g;

// Phone number regex to validate input as only numbers
export const PHONE_REGEX = /^[0-9\-()+\s]*$/;

// Check if value is not only spaces
export const NO_ONLY_SPACES_REGEX = /\S+/;
